/**
 * View settings for the Calendar page (docs/44 §3.3, D-P6-5/D-P6-6).
 *
 * Shaped exactly like the future server-side `user_settings.calendar_display`
 * namespace so the eventual wiring is a storage swap, not a redesign.
 *
 * TODO(calendar_display): the ratified home for these settings is the
 * `user_settings.calendar_display` namespace on migration 0035, which is
 * blocked on a post-0034 database dump (§0.2c rule 3). Until that lands the
 * page holds them in React state initialised from these defaults; they do not
 * survive a reload. Wire through PreferencesContext once the namespace exists.
 */

export type CalendarViewMode = 'day' | 'three_day' | 'week';

export type CalendarWeekStart = 'monday' | 'sunday';

/** The five drawable layers, matching the range endpoint's CALENDAR_LAYERS. */
export const CALENDAR_LAYER_KEYS = [
  'hour_classes',
  'planned_events',
  'commitment_occurrences',
  'task_blocks',
  'time_entries',
] as const;

export type CalendarLayerKey = (typeof CALENDAR_LAYER_KEYS)[number];

export const CALENDAR_LAYER_LABELS: Record<CalendarLayerKey, string> = {
  hour_classes: 'Hour classes',
  planned_events: 'Imported events',
  commitment_occurrences: 'Commitments',
  task_blocks: 'Task blocks',
  time_entries: 'Logged time',
};

export interface CalendarDisplaySettings {
  default_view: CalendarViewMode;
  week_start: CalendarWeekStart;
  /** Visible-window edges, minutes since the day opened (06:00–23:00). */
  visible_start_minute: number;
  visible_end_minute: number;
  show_full_day: boolean;
  compare_plan_actual: boolean;
  show_hour_classes: boolean;
  show_planned_events: boolean;
  show_commitment_occurrences: boolean;
  show_task_blocks: boolean;
  show_time_entries: boolean;
}

export const CALENDAR_DISPLAY_DEFAULTS: CalendarDisplaySettings = {
  default_view: 'week',
  week_start: 'monday',
  visible_start_minute: 360,
  visible_end_minute: 1380,
  show_full_day: false,
  compare_plan_actual: false,
  show_hour_classes: true,
  show_planned_events: true,
  show_commitment_occurrences: true,
  show_task_blocks: true,
  show_time_entries: true,
};

/** The boolean visibility field each layer toggles. */
export type CalendarLayerSettingKey =
  | 'show_hour_classes'
  | 'show_planned_events'
  | 'show_commitment_occurrences'
  | 'show_task_blocks'
  | 'show_time_entries';

export const LAYER_SETTING_KEYS: Record<
  CalendarLayerKey,
  CalendarLayerSettingKey
> = {
  hour_classes: 'show_hour_classes',
  planned_events: 'show_planned_events',
  commitment_occurrences: 'show_commitment_occurrences',
  task_blocks: 'show_task_blocks',
  time_entries: 'show_time_entries',
};

/** Days a view mode spans, for windowing and prev/next navigation. */
export const VIEW_MODE_DAYS: Record<CalendarViewMode, number> = {
  day: 1,
  three_day: 3,
  week: 7,
};
